// ============================================================================
// ConfirmPage — public attendance confirmation landing.
// ============================================================================
// Routed via #confirm/<token>. Hits GET /api/registrations/confirm/:token,
// which (idempotently) flips confirmed_at and returns event details. Three
// possible UI states:
//   1. Loading — spinning indicator while we hit the backend
//   2. Success — green check + event details + thanks copy
//   3. Error — red banner if token is unknown / expired
//
// No auth required: this page is reachable from the email link any registrant
// receives. Safe to bookmark — re-visits don't break anything.
// ============================================================================

import { useEffect, useState } from 'react';
import { CheckCircle2, AlertCircle, Loader2, MapPin, Calendar } from 'lucide-react';
import { api, type ConfirmRegistrationResult } from '../api';

interface Props {
  token: string;
}

export function ConfirmPage({ token }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ConfirmRegistrationResult | null>(null);

  useEffect(() => {
    setLoading(true); setError(''); setResult(null);
    api.confirmRegistration(token)
      .then((r) => setResult(r))
      .catch((err) => setError(err?.message || 'Could not confirm.'))
      .finally(() => setLoading(false));
  }, [token]);

  const formatDateRange = (s: string, e: string): string => {
    const start = new Date(s);
    const end = new Date(e);
    const opts: Intl.DateTimeFormatOptions = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' };
    const startStr = start.toLocaleDateString('en-US', opts);
    const endStr = end.toLocaleDateString('en-US', opts);
    if (startStr === endStr) return startStr;
    return `${startStr} — ${endStr}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center">
          <a href="/" className="flex items-center gap-3" aria-label="Up in Farms — home">
            <img src="/logo.jpg" alt="Up in Farms Food Hub" className="h-12 md:h-14 w-auto" />
          </a>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto px-4 py-12 w-full">
        {loading && (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-uif-green mx-auto mb-4" />
            <p className="text-sm text-gray-600">Confirming your attendance…</p>
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <AlertCircle className="h-10 w-10 text-red-700 mx-auto mb-3" />
            <h1 className="text-lg font-semibold text-red-900 mb-2">Confirmation link not recognized</h1>
            <p className="text-sm text-red-800 mb-4">{error}</p>
            <p className="text-xs text-red-700">
              Double-check the link in your email — it should look like{' '}
              <code className="bg-white px-1 rounded">events.upinfarms.com/#confirm/&lt;long-id&gt;</code>.
              If it still doesn't work, reply to the email or contact{' '}
              <a href="mailto:support@upinfarms.com" className="underline">support@upinfarms.com</a> and we'll sort it out.
            </p>
          </div>
        )}

        {!loading && result && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className={
              'px-6 py-8 text-center ' +
              (result.already_confirmed ? 'bg-uif-green/5' : 'bg-uif-green/10')
            }>
              <CheckCircle2 className="h-12 w-12 text-uif-green mx-auto mb-3" />
              <h1 className="text-2xl font-bold text-uif-green-dark mb-1">
                {result.already_confirmed ? 'Already confirmed' : 'Thanks for confirming!'}
              </h1>
              <p className="text-sm text-gray-700">
                {result.already_confirmed
                  ? `We've got you down for ${result.event_title}. See you there!`
                  : `Welcome to ${result.event_title}, ${(result.full_name.split(/\s+/)[0] || result.full_name).trim()}.`
                }
              </p>
            </div>

            <div className="border-t border-gray-200 px-6 py-5 space-y-3">
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-uif-green-dark mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">When</div>
                  <div className="text-sm text-gray-900 font-medium">{formatDateRange(result.start_date, result.end_date)}</div>
                </div>
              </div>
              {result.location && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-uif-green-dark mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Where</div>
                    <div className="text-sm text-gray-900 font-medium">{result.location}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 text-xs text-gray-600">
              <p className="mb-1">
                <strong>Need to cancel or change something?</strong> Reply to the email you received,
                or contact <a href="mailto:support@upinfarms.com" className="text-uif-green underline">support@upinfarms.com</a>.
              </p>
              <p>We'll send a reminder a day before the event.</p>
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-gray-200 bg-white py-6">
        <div className="max-w-3xl mx-auto px-4 text-center text-xs text-gray-500">
          Up in Farms LLC · 2093 Lakeland Dr, Jackson, MS 39216 ·{' '}
          <a href="mailto:support@upinfarms.com" className="text-uif-green hover:underline">support@upinfarms.com</a>
        </div>
      </footer>
    </div>
  );
}
